import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Editor, { useMonaco, type Monaco } from '@monaco-editor/react';
import type * as MonacoNS from 'monaco-editor';
import { useEditorStore } from '@/store/editorStore';
import { queryApi } from '@/api/query';
import { connectionsApi } from '@/api/connections';
import type { ColumnDesignInfo, DatabaseObjectInfo, DeniedResponse } from '@/types/domain';
import './SqlEditor.css';

// ── IntelliSense cache (module-level: Monaco language providers are global) ──

interface IsCache {
  connectionId: string | null;
  objects: DatabaseObjectInfo[];
  columns: Map<string, ColumnDesignInfo[]>;
}

const _is: IsCache = { connectionId: null, objects: [], columns: new Map() };
let _completionDisposable: { dispose(): void } | null = null;

const SQL_KEYWORDS = [
  'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN',
  'JOIN', 'INNER JOIN', 'LEFT JOIN', 'LEFT OUTER JOIN', 'RIGHT JOIN', 'FULL OUTER JOIN', 'CROSS JOIN',
  'ON', 'AS', 'GROUP BY', 'ORDER BY', 'HAVING', 'DISTINCT', 'ASC', 'DESC',
  'TOP', 'LIMIT', 'OFFSET', 'FETCH NEXT', 'ROWS ONLY',
  'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE FROM', 'MERGE INTO',
  'CREATE TABLE', 'ALTER TABLE', 'DROP TABLE', 'TRUNCATE TABLE',
  'IS NULL', 'IS NOT NULL', 'EXISTS', 'NOT EXISTS', 'ALL', 'ANY',
  'UNION', 'UNION ALL', 'EXCEPT', 'INTERSECT',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'WITH', 'BEGIN', 'COMMIT', 'ROLLBACK',
  'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'COUNT(*)',
  'CAST', 'CONVERT', 'COALESCE', 'ISNULL', 'NULLIF',
  'GETDATE', 'NOW', 'CURRENT_TIMESTAMP', 'DATEADD', 'DATEDIFF', 'FORMAT',
  'LEN', 'LENGTH', 'SUBSTRING', 'CHARINDEX', 'REPLACE', 'TRIM', 'UPPER', 'LOWER',
  'ROUND', 'FLOOR', 'CEILING', 'ABS',
  'VARCHAR', 'NVARCHAR', 'INT', 'BIGINT', 'DECIMAL', 'FLOAT', 'BIT', 'DATETIME', 'DATE', 'TEXT',
];

function colDetail(col: ColumnDesignInfo): string {
  let t = col.dataType;
  if (col.maxLength != null)
    t += `(${col.maxLength === -1 ? 'MAX' : col.maxLength})`;
  else if (col.numericPrecision != null && col.numericScale != null && col.numericScale > 0)
    t += `(${col.numericPrecision},${col.numericScale})`;
  return `${t}${col.nullable ? '' : ' NOT NULL'}${col.isPrimaryKey ? ' 🔑' : ''}`;
}

// ─────────────────────────────────────────────────────────────────────────────

export function SqlEditor() {
  const tabs                = useEditorStore(s => s.tabs);
  const activeTabId         = useEditorStore(s => s.activeTabId);
  const newTab              = useEditorStore(s => s.newTab);
  const closeTab            = useEditorStore(s => s.closeTab);
  const setActiveTab        = useEditorStore(s => s.setActiveTab);
  const updateTabSql        = useEditorStore(s => s.updateTabSql);
  const setExecuting        = useEditorStore(s => s.setExecuting);
  const setLastResponse     = useEditorStore(s => s.setLastResponse);
  const isExecuting         = useEditorStore(s => s.isExecuting);
  const connections         = useEditorStore(s => s.connections);
  const selectedConnectionId = useEditorStore(s => s.selectedConnectionId);

  const activeTab    = tabs.find(t => t.id === activeTabId);
  const selectedConn = connections.find(c => c.id === selectedConnectionId);
  const executeRef   = useRef<(() => void) | undefined>(undefined);
  const abortRef     = useRef<AbortController | null>(null);
  const editorRef    = useRef<MonacoNS.editor.IStandaloneCodeEditor | null>(null);
  const monacoApi    = useMonaco();
  const [isDragOver, setIsDragOver] = useState(false);

  // Refresh IntelliSense object list when connection changes
  useEffect(() => {
    if (!selectedConnectionId) {
      _is.connectionId = null;
      _is.objects = [];
      _is.columns.clear();
      return;
    }
    if (selectedConnectionId === _is.connectionId) return;
    _is.connectionId = selectedConnectionId;
    _is.columns.clear();
    connectionsApi.listObjects(selectedConnectionId)
      .then(objs => { _is.objects = objs; })
      .catch(() => {});
  }, [selectedConnectionId]);

  async function execute() {
    const connId = selectedConnectionId;
    if (!activeTab) return;
    if (!connId) {
      alert('Selecione uma conexão na barra lateral antes de executar.');
      return;
    }
    abortRef.current = new AbortController();
    setExecuting(true);
    setLastResponse(undefined as never);
    try {
      const resp = await queryApi.execute({
        connectionId: connId,
        sqlText: activeTab.sqlText,
        maxRows: 500,
      }, abortRef.current.signal);
      setLastResponse(resp);
    } catch (err: unknown) {
      if (axios.isCancel(err) || (err instanceof DOMException && err.name === 'AbortError')) {
        setLastResponse({
          queryId: '', status: 'Error', columns: [], rows: [],
          rowsAffected: 0, durationMs: 0, errorMessage: 'Consulta cancelada pelo usuário.',
          warnings: [], maskedColumns: {}, matchedRules: [],
        });
      } else if (axios.isAxiosError(err) && err.response?.status === 403) {
        setLastResponse(err.response.data as DeniedResponse);
      } else {
        const msg = err instanceof Error ? err.message : String(err);
        setLastResponse({
          queryId: '', status: 'Error', columns: [], rows: [],
          rowsAffected: 0, durationMs: 0, errorMessage: msg,
          warnings: [], maskedColumns: {}, matchedRules: [],
        });
      }
    } finally {
      abortRef.current = null;
      setExecuting(false);
    }
  }

  function cancelExecute() {
    abortRef.current?.abort();
  }

  executeRef.current = execute;

  function handleEditorDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(false);
    const text = e.dataTransfer.getData('text/plain');
    if (!text || !editorRef.current || !monacoApi) return;
    const target = editorRef.current.getTargetAtClientPoint(e.clientX, e.clientY);
    const pos = target?.position ?? editorRef.current.getPosition() ?? { lineNumber: 1, column: 1 };
    editorRef.current.executeEdits('drag-drop', [{
      range: new monacoApi.Range(pos.lineNumber, pos.column, pos.lineNumber, pos.column),
      text,
      forceMoveMarkers: true,
    }]);
    editorRef.current.focus();
  }

  function handleEditorMount(editorInstance: MonacoNS.editor.IStandaloneCodeEditor, monaco: Monaco) {
    editorRef.current = editorInstance;
    // Fix character overlap with custom fonts
    document.fonts.ready.then(() => monaco.editor.remeasureFonts());

    // Re-register completion provider (handles HMR re-mounts)
    _completionDisposable?.dispose();
    _completionDisposable = monaco.languages.registerCompletionItemProvider('sql', {
      triggerCharacters: ['.'],

      provideCompletionItems: async (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        // Text from line start to cursor — used for context detection
        const lineText = model.getValueInRange({
          startLineNumber: position.lineNumber, startColumn: 1,
          endLineNumber: position.lineNumber,   endColumn: position.column,
        });

        // ── UPDATE ... SET context: show only that table's columns ──────────────
        const textToCursor = model.getValueInRange({
          startLineNumber: 1, startColumn: 1,
          endLineNumber: position.lineNumber, endColumn: position.column,
        });
        const updateMatch = textToCursor.match(
          /\bUPDATE\s+(?:\w+\.)?(\w+)(?:\s+\w+)?\s+SET\b([\s\S]*)$/i
        );
        if (updateMatch) {
          const tableName = updateMatch[1].toLowerCase();
          const afterSet  = updateMatch[2];
          // Last segment = text after the last comma (or the whole afterSet if no comma).
          // If it has no '=' the cursor is on a column name, not a value.
          const lastSeg = afterSet.includes(',')
            ? afterSet.substring(afterSet.lastIndexOf(',') + 1)
            : afterSet;
          if (!lastSeg.includes('=')) {
            const obj = _is.objects.find(o =>
              (o.objectType === 'Table' || o.objectType === 'View') &&
              o.name.toLowerCase() === tableName
            );
            if (obj) {
              const key = `${obj.schema}.${obj.name}`;
              if (!_is.columns.has(key) && _is.connectionId) {
                try {
                  const cols = await connectionsApi.getTableDesign(_is.connectionId, obj.schema, obj.name);
                  _is.columns.set(key, cols);
                } catch { /* ignore */ }
              }
              const cols = _is.columns.get(key) ?? [];
              if (cols.length > 0) {
                return {
                  suggestions: cols.map(col => ({
                    label: col.name,
                    kind: monaco.languages.CompletionItemKind.Field,
                    insertText: col.name,
                    range,
                    detail: colDetail(col),
                  })),
                };
              }
            }
          }
        }

        // ── Column completion: "tableName.colPrefix" ──────────────────────────
        const dotMatch = lineText.match(/(\w+)\.(\w*)$/);
        if (dotMatch) {
          const prefix = dotMatch[1].toLowerCase();
          const obj = _is.objects.find(
            o => (o.objectType === 'Table' || o.objectType === 'View') &&
                 (o.name.toLowerCase() === prefix || o.schema.toLowerCase() === prefix)
          );
          if (obj) {
            const key = `${obj.schema}.${obj.name}`;
            if (!_is.columns.has(key) && _is.connectionId) {
              try {
                const cols = await connectionsApi.getTableDesign(_is.connectionId, obj.schema, obj.name);
                _is.columns.set(key, cols);
              } catch { /* ignore */ }
            }
            const cols = _is.columns.get(key) ?? [];
            return {
              suggestions: cols.map(col => ({
                label: col.name,
                kind: monaco.languages.CompletionItemKind.Field,
                insertText: col.name,
                range,
                detail: colDetail(col),
              })),
            };
          }
          return { suggestions: [] };
        }

        // ── Keywords + database objects ───────────────────────────────────────
        const suggestions: MonacoNS.languages.CompletionItem[] = [];

        for (const kw of SQL_KEYWORDS) {
          suggestions.push({
            label: kw,
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: kw,
            range,
            sortText: `0_${kw}`,
          });
        }

        for (const obj of _is.objects) {
          const kindMap: Record<string, number> = {
            Table:     monaco.languages.CompletionItemKind.Class,
            View:      monaco.languages.CompletionItemKind.Interface,
            Procedure: monaco.languages.CompletionItemKind.Function,
            Function:  monaco.languages.CompletionItemKind.Function,
          };
          const kind = kindMap[obj.objectType] ?? monaco.languages.CompletionItemKind.Value;

          suggestions.push({
            label: obj.name,
            kind,
            insertText: obj.name,
            range,
            detail: `${obj.schema} · ${obj.objectType}`,
            sortText: `1_${obj.name}`,
          });
          suggestions.push({
            label: `${obj.schema}.${obj.name}`,
            kind,
            insertText: `${obj.schema}.${obj.name}`,
            range,
            detail: obj.objectType,
            sortText: `2_${obj.schema}.${obj.name}`,
          });
        }

        return { suggestions };
      },
    });

    monaco.editor.addEditorAction({
      id: 'sqlops.execute',
      label: 'Executar Query',
      keybindings: [monaco.KeyCode.F5],
      run: () => executeRef.current?.(),
    });
  }

  const envLabel = selectedConn
    ? { Production: 'PROD', Homologation: 'HML', Development: 'DEV' }[selectedConn.environment] ?? ''
    : '';
  const envClass = selectedConn
    ? { Production: 'prod', Homologation: 'warn', Development: 'dev' }[selectedConn.environment] ?? ''
    : '';

  return (
    <section className="editor-area">
      <div className="tabs">
        {tabs.map(t => (
          <div
            key={t.id}
            className={`tab ${t.id === activeTabId ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            <span className={`tab-dot ${t.dirty ? 'dirty' : ''}`}>●</span>
            <span>{t.title}</span>
            <span
              className="tab-close"
              onClick={e => { e.stopPropagation(); closeTab(t.id); }}
            >×</span>
          </div>
        ))}
        <div className="tab-add" onClick={() => newTab()}>+</div>
      </div>

      <div className="editor-toolbar">
        <button
          className="btn primary"
          onClick={execute}
          disabled={isExecuting || !selectedConnectionId}
          title={!selectedConnectionId ? 'Selecione uma conexão' : 'Executar (F5)'}
        >
          {isExecuting ? '⏳' : '▷'} Executar <span className="kbd">F5</span>
        </button>
        <button className="btn" disabled={!isExecuting} onClick={cancelExecute}>⏹ Cancelar</button>
        <button className="btn">↻ Plano</button>
        <div className="toolbar-divider" />
        <button className="btn warn">⚠ Pedir aprovação</button>
        <button className="btn">↓ Exportar</button>

        <div className="conn-pill">
          <span className={`conn-dot ${selectedConn ? 'connected' : ''}`} />
          {selectedConn
            ? <><span>{selectedConn.name} / {selectedConn.database}</span>
                <span className="conn-sep">•</span>
                <span className={`conn-env ${envClass}`}>{envLabel}</span></>
            : <span style={{ color: 'var(--text-muted)' }}>nenhuma conexão</span>
          }
        </div>
      </div>

      <div
        className={`editor-monaco${isDragOver ? ' drag-over' : ''}`}
        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
        onDragEnter={() => setIsDragOver(true)}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false); }}
        onDrop={handleEditorDrop}
      >
        <Editor
          height="100%"
          language="sql"
          theme="vs-dark"
          value={activeTab?.sqlText ?? ''}
          onChange={v => activeTab && updateTabSql(activeTab.id, v ?? '')}
          onMount={handleEditorMount}
          options={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 13,
            fontLigatures: false,
            minimap: { enabled: false },
            lineNumbersMinChars: 4,
            renderLineHighlight: 'gutter',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            quickSuggestions: { other: true, comments: false, strings: false },
            suggestOnTriggerCharacters: true,
            acceptSuggestionOnEnter: 'on',
            wordBasedSuggestions: 'off',
          }}
        />
      </div>
    </section>
  );
}
